import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Home, ArrowRight, Users, Key, Banknote, FileText, Lock, Sparkles, Eye, Clock, BookOpen, ChevronRight } from "lucide-react";

const TenUResLogo = ({ size = "text-4xl" }) => (
  <span className={`tenures-logo ${size}`}>
    Ten<span className="tenures-ur font-extrabold">UR</span>es
  </span>
);

const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-6 border border-gray-200 hover:border-[#05D38A] hover:shadow-lg transition-all duration-300"
  >
    <div className="w-12 h-12 rounded-lg bg-[#05D38A]/10 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-[#05D38A]" strokeWidth={1.5} />
    </div>
    <h3 className="text-xl font-bold text-[#111827] mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

const TrustBadge = ({ icon: Icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
    className="flex items-start gap-4"
  >
    <div className="w-12 h-12 rounded-full bg-[#05D38A]/10 flex items-center justify-center shrink-0">
      <Icon className="w-6 h-6 text-[#05D38A]" strokeWidth={1.5} />
    </div>
    <div>
      <h3 className="font-bold text-[#111827] mb-1">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  </motion.div>
);

const LibraryCard = ({ icon: Icon, title, readTime, category, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
    className="bg-white rounded-xl border border-gray-200 hover:border-[#05D38A] hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer group"
  >
    <div className="h-32 bg-gradient-to-br from-[#05D38A]/10 to-[#05D38A]/5 flex items-center justify-center">
      <Icon className="w-12 h-12 text-[#05D38A] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
    </div>
    <div className="p-5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium text-[#05D38A] bg-[#05D38A]/10 px-2 py-1 rounded">{category}</span>
        <span className="text-xs text-gray-400">{readTime}</span>
      </div>
      <h3 className="font-bold text-[#111827] group-hover:text-[#05D38A] transition-colors">{title}</h3>
      <div className="flex items-center gap-1 mt-3 text-[#05D38A] text-sm font-medium">
        Read Guide <ChevronRight className="w-4 h-4" />
      </div>
    </div>
  </motion.div>
);

export default function Landing() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Shield,
      title: "Secure Your Family",
      description: "Understand your succession rights and protect your loved ones' home."
    },
    {
      icon: Users,
      title: "Find Your Match",
      description: "Anonymous property matching based on lifestyle, not just location."
    },
    {
      icon: Banknote,
      title: "Claim Your Bonus",
      description: "Discover cash incentives available for rightsizing your home."
    },
    {
      icon: Key,
      title: "Own Your Home",
      description: "Track your path to ownership with Right to Buy guidance."
    }
  ];

  const trustBadges = [
    {
      icon: FileText,
      title: "Professional Terminology",
      description: "Our guides use the same language your Housing Officer uses. No translation needed."
    },
    {
      icon: Eye,
      title: "Anonymous Matching",
      description: "Your personal details stay private until you are ready to connect."
    },
    {
      icon: Clock,
      title: "7 Years Expertise",
      description: "Built on a decade of frontline social housing management experience."
    }
  ];

  const libraryGuides = [
    {
      icon: Users,
      title: "The 3-Step Guide to a Successful Housing Officer Meeting",
      readTime: "5 min read",
      category: "Meetings"
    },
    {
      icon: Banknote,
      title: "Understanding the Cost Floor: Why Your Discount Might Be Capped",
      readTime: "7 min read",
      category: "Right to Buy"
    },
    {
      icon: Shield,
      title: "Succession vs. Assignment: What's the Difference for Your Family?",
      readTime: "6 min read",
      category: "Succession"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <TenUResLogo size="text-2xl" />
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-[#111827] hover:text-[#05D38A] font-medium"
              onClick={() => navigate('/login')}
              data-testid="nav-login-btn"
            >
              Sign In
            </Button>
            <Button 
              className="bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold glow-mint"
              onClick={() => navigate('/register')}
              data-testid="nav-register-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#111827] mb-6">
              Are you a social housing resident?
            </h1>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-gray-600 mb-4 max-w-2xl mx-auto"
          >
            <TenUResLogo size="text-xl sm:text-2xl" /> is your passport to living in and enjoying your home.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-base text-[#05D38A] font-semibold mb-10"
          >
            It's <span className="tenures-ur font-bold">UR</span>s.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Button 
              size="lg"
              className="bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold text-lg px-8 py-6 rounded-lg glow-mint"
              onClick={() => navigate('/register')}
              data-testid="hero-cta-btn"
            >
              Create My Secure Passport
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>

        {/* Decorative gradient */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#05D38A]/5 rounded-full blur-3xl pointer-events-none"></div>
      </section>

      {/* Trust Badges Section - NEW */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50 border-y border-gray-200">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-[#111827] mb-3">
              Officer-Ready Documentation
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Unlike generic calculators, <TenUResLogo size="text-base" /> prepares you for real conversations with professionals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trustBadges.map((badge, index) => (
              <TrustBadge key={badge.title} {...badge} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
              Your 4 Pillars of Housing Security
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Navigate your housing rights with confidence. Each pillar helps you understand and protect what's <span className="tenures-ur font-semibold">UR</span>s.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <FeatureCard key={feature.title} {...feature} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* TenURes Library Section - NEW */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12"
          >
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-5 h-5 text-[#05D38A]" />
                <span className="text-sm font-semibold text-[#05D38A] uppercase tracking-wide">TenURes Library</span>
              </div>
              <h2 className="text-3xl font-bold text-[#111827]">
                Knowledge is Power
              </h2>
              <p className="text-gray-600 mt-2">
                Expert guides written in plain English to help you navigate the system.
              </p>
            </div>
            <Button variant="outline" className="border-[#05D38A] text-[#05D38A] hover:bg-[#05D38A]/5">
              View All Guides
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {libraryGuides.map((guide, index) => (
              <LibraryCard key={guide.title} {...guide} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Why TenURes Section - NEW */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-[#05D38A]/10 px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#05D38A]" />
              <span className="text-sm font-semibold text-[#05D38A]">Why TenURes?</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-6">
              We Don't Just Give You a Number.<br />
              <span className="text-[#05D38A]">We Give You a Passport.</span>
            </h2>
            
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              While others provide generic calculators, <TenUResLogo size="text-lg" /> provides a structured, professional framework for your housing future. We don't just tell you what you might be entitled to — <strong className="text-[#111827]">we prepare you to claim it.</strong>
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto text-left">
              <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-400 text-sm">✗</span>
                </div>
                <span className="text-gray-500">Generic online calculators</span>
              </div>
              <div className="flex items-center gap-3 bg-[#05D38A]/5 p-4 rounded-lg border border-[#05D38A]/20">
                <div className="w-8 h-8 rounded-full bg-[#05D38A] flex items-center justify-center">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-[#111827] font-medium">Officer-ready documentation</span>
              </div>
              <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-400 text-sm">✗</span>
                </div>
                <span className="text-gray-500">Use once and leave</span>
              </div>
              <div className="flex items-center gap-3 bg-[#05D38A]/5 p-4 rounded-lg border border-[#05D38A]/20">
                <div className="w-8 h-8 rounded-full bg-[#05D38A] flex items-center justify-center">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-[#111827] font-medium">Track your entire journey</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto bg-white rounded-2xl p-8 sm:p-12 text-center border border-gray-200 shadow-lg relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#05D38A]/5 to-transparent pointer-events-none"></div>
          <div className="relative z-10">
            <Home className="w-16 h-16 text-[#05D38A] mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
              Ready to protect your home?
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Join thousands of residents who've discovered their housing rights with <TenUResLogo size="text-lg" />.
            </p>
            <Button 
              size="lg"
              className="bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold text-lg px-8 py-6 rounded-lg glow-mint"
              onClick={() => navigate('/register')}
              data-testid="cta-bottom-btn"
            >
              Get Your Passport Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </motion.div>
      </section>

      {/* Footer with Credibility Blurb */}
      <footer className="py-12 px-4 border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Credibility Blurb */}
          <div className="max-w-2xl mx-auto text-center mb-8">
            <p className="text-[#05D38A] font-semibold text-sm uppercase tracking-wide mb-3">
              Built by Housing Professionals, for Residents
            </p>
            <p className="text-gray-600 leading-relaxed">
              With over 7 years of frontline experience as a Housing Officer, I created <TenUResLogo size="text-base" /> to give you the clarity, security, and professional edge you need to manage your home. It's not just an app; it's your rights, simplified.
            </p>
          </div>
          
          {/* Footer Links */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8 pt-6 border-t border-gray-200">
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><span onClick={() => navigate('/register')} className="hover:text-[#05D38A] cursor-pointer">Get Started</span></li>
                <li><span onClick={() => navigate('/login')} className="hover:text-[#05D38A] cursor-pointer">Sign In</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><span onClick={() => navigate('/library')} className="hover:text-[#05D38A] cursor-pointer">Knowledge Hub</span></li>
                <li><span onClick={() => navigate('/about')} className="hover:text-[#05D38A] cursor-pointer">About Us</span></li>
                <li><span onClick={() => navigate('/providers')} className="hover:text-[#05D38A] cursor-pointer">For Providers</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><span onClick={() => navigate('/privacy')} className="hover:text-[#05D38A] cursor-pointer">Privacy & Security</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><span onClick={() => navigate('/contact')} className="hover:text-[#05D38A] cursor-pointer">Get in Touch</span></li>
                <li><span className="text-gray-400">hello@tenures.app</span></li>
              </ul>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-gray-200">
            <TenUResLogo size="text-xl" />
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} TenURes. Advisory tool for social housing residents.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
